import React, { useCallback, useEffect, useState } from 'react';
import { Avatar, Box, Card, CardActionArea, Chip, Divider, Typography } from '@mui/material';
import PersonOutlineOutlined from '@mui/icons-material/PersonOutlineOutlined';
import ReceiptLongOutlined from '@mui/icons-material/ReceiptLongOutlined';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import { useNavigate } from 'react-router-dom';

import Button from '../components/Button';
import PageContainer from '../layouts/PageContainer';
import PageState from '../layouts/PageState';
import { V2_PRIMARY_MAIN } from '../design/muiTheme';
import { V2ApiError } from '../services/client';
import { fetchUserOrders } from '../services/orders';
import { useV2Session } from '../state/SessionProvider';

const ORDER_PAGE_SIZE = 10;

/** Pretty label for `order_status` strings coming from the Pilot server. */
function formatOrderStatus(raw) {
  const s = String(raw || 'pending').toLowerCase();
  return s
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/** MUI Chip color mapping for status badges. */
function statusChipColor(raw) {
  const s = String(raw || '').toLowerCase();
  if (s === 'delivered' || s === 'completed') return 'success';
  if (s === 'cancelled' || s === 'returned') return 'error';
  if (s === 'out_for_delivery') return 'info';
  if (s === 'confirmed' || s === 'processing') return 'primary';
  return 'default';
}

/** Render the order's "placed at" timestamp (millis or ISO string). */
function formatPlacedAt(order) {
  const raw = order?.timeStamp ?? order?.createdAt;
  if (raw == null) return null;
  const d = new Date(typeof raw === 'number' ? raw : raw);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Short text describing what's in the order, e.g. "2× Tender CC, 1× Eggs". */
function summarizeProducts(products) {
  if (!Array.isArray(products) || products.length === 0) return null;
  const head = products
    .slice(0, 2)
    .map((p) => {
      const name = p?.name || 'Item';
      const qty = Number(p?.quantity) || 1;
      return qty > 1 ? `${qty}× ${name}` : name;
    })
    .join(', ');
  const remaining = products.length - 2;
  return remaining > 0 ? `${head} +${remaining} more` : head;
}

function OrderCard({ order, onOpen }) {
  const total = Number(order?.total ?? order?.order_amount ?? 0);
  const placedAt = formatPlacedAt(order);
  const summary = summarizeProducts(order?.products);
  const status = order?.order_status || 'pending';
  const id = order?.orderId || order?.id;
  const canOpen = Boolean(id) && typeof onOpen === 'function';

  return (
    <Card
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        mb: 1.5,
        bgcolor: 'background.paper',
        overflow: 'hidden',
      }}
    >
      <CardActionArea
        disabled={!canOpen}
        onClick={() => canOpen && onOpen(String(id))}
        sx={{
          p: 1.75,
          alignItems: 'stretch',
          textAlign: 'left',
          ...(canOpen ? { cursor: 'pointer' } : {}),
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 0.75 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: 0.4, fontSize: 11 }}
            >
              Order ID
            </Typography>
            <Typography
              variant="body1"
              fontWeight={700}
              sx={{
                color: V2_PRIMARY_MAIN,
                wordBreak: 'break-all',
                lineHeight: 1.25,
              }}
            >
              {id || '—'}
            </Typography>
          </Box>
          <Chip
            size="small"
            color={statusChipColor(status)}
            label={formatOrderStatus(status)}
            sx={{ fontWeight: 600, flexShrink: 0 }}
          />
        </Box>

        {summary ? (
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            {summary}
          </Typography>
        ) : null}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1, mt: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            {placedAt || '—'}
          </Typography>
          <Typography variant="subtitle1" fontWeight={800}>
            ₹{total}
          </Typography>
        </Box>
      </CardActionArea>
    </Card>
  );
}

export default function Account() {
  const { mobile, user, logout } = useV2Session();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [orders, setOrders] = useState([]);
  const [visibleCount, setVisibleCount] = useState(ORDER_PAGE_SIZE);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [ordersError, setOrdersError] = useState(null);

  const loadOrders = useCallback(async () => {
    if (!mobile) {
      setOrders([]);
      setVisibleCount(ORDER_PAGE_SIZE);
      setLoadingOrders(false);
      return;
    }
    setLoadingOrders(true);
    setOrdersError(null);
    try {
      const list = await fetchUserOrders(mobile);
      setOrders(list);
      setVisibleCount(ORDER_PAGE_SIZE);
    } catch (err) {
      const msg = err instanceof V2ApiError ? err.message : 'Could not load your orders.';
      setOrdersError(msg);
    } finally {
      setLoadingOrders(false);
    }
  }, [mobile]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const handleLogout = async () => {
    setBusy(true);
    try {
      await logout();
      navigate('/v2', { replace: true });
    } finally {
      setBusy(false);
    }
  };

  const displayName = user?.name || user?.f_name || user?.userName || null;
  const initial = (displayName || '?').trim().charAt(0).toUpperCase();

  const visibleOrders = orders.slice(0, visibleCount);
  const remainingCount = Math.max(0, orders.length - visibleOrders.length);
  const nextBatchSize = Math.min(ORDER_PAGE_SIZE, remainingCount);
  const handleShowMore = () => {
    setVisibleCount((c) => Math.min(c + ORDER_PAGE_SIZE, orders.length));
  };

  return (
    <PageContainer title="Profile">
      <Card
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          p: 2,
          mb: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Avatar
          sx={{
            bgcolor: V2_PRIMARY_MAIN,
            color: '#fff',
            width: 56,
            height: 56,
            fontWeight: 700,
            fontSize: 22,
          }}
        >
          {displayName ? initial : <PersonOutlineOutlined />}
        </Avatar>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          {displayName ? (
            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2, mb: 0.25 }}>
              {displayName}
            </Typography>
          ) : (
            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2, mb: 0.25 }}>
              Signed in
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            +91 {mobile}
          </Typography>
        </Box>
      </Card>

      <Button
        variant="outlined"
        onClick={() => navigate('/v2/account/addresses')}
        endIcon={<ChevronRightRounded />}
        sx={{
          mb: 2,
          width: '100%',
          justifyContent: 'space-between',
          textTransform: 'none',
          px: 1.5,
          py: 1.25,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: '#F8FAFC',
          color: 'text.primary',
          fontWeight: 500,
          '&:hover': {
            bgcolor: '#F8FAFC',
            borderColor: 'divider',
          },
        }}
      >
        My Addresses
      </Button>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
        <ReceiptLongOutlined fontSize="small" sx={{ color: 'text.secondary' }} />
        <Typography variant="subtitle1" fontWeight={700}>
          Recent orders
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
          {orders.length > 0 ? `Showing ${visibleOrders.length} of ${orders.length}` : null}
        </Typography>
      </Box>

      <PageState
        loading={loadingOrders}
        error={ordersError}
        onRetry={loadOrders}
        loadingMessage="Loading your orders…"
        errorTitle={ordersError || 'Could not load orders'}
      >
        {orders.length === 0 ? (
          <Box
            sx={{
              border: '1px dashed',
              borderColor: 'divider',
              borderRadius: 2,
              py: 4,
              px: 2,
              textAlign: 'center',
              mb: 3,
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              You haven't placed any orders yet.
            </Typography>
            <Button variant="outlined" onClick={() => navigate('/v2/catalog')} sx={{ borderRadius: '5px' }}>
              Browse catalog
            </Button>
          </Box>
        ) : (
          <Box sx={{ mb: 3 }}>
            {visibleOrders.map((order) => (
              <OrderCard
                key={String(order?.orderId || order?.id)}
                order={order}
                onOpen={(oid) => navigate(`/v2/account/orders/${encodeURIComponent(oid)}`)}
              />
            ))}
            {remainingCount > 0 ? (
              <Button
                variant="outlined"
                fullWidth
                onClick={handleShowMore}
                sx={{ borderRadius: '5px', mt: 0.5 }}
              >
                Show more {nextBatchSize === 1 ? 'order' : 'orders'}
              </Button>
            ) : null}
          </Box>
        )}
      </PageState>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5}}>
        <Button
          variant="outlined"
          color="error"
          fullWidth
          disabled={busy}
          onClick={handleLogout}
          sx={{ borderRadius: '5px' }}
        >
          {busy ? 'Signing out…' : 'Sign out'}
        </Button>
      </Box>
    </PageContainer>
  );
}
